import { useLayoutEffect, useMemo, useRef, useState } from 'react';

import { trackEvent } from '../../utils/gt';
import { lastWeeks } from '../../utils/progress';
import { useProgressFormat } from './useProgressFormat';

const WEEKS = 12;
const DURATION_STEPS = [900, 1800, 3600, 7200, 10800, 18000, 36000];

// Three gridline steps: whole minutes or hours for duration, 1-2-5 × 10ⁿ kg for volume
const weekAxisStep = (max, isDuration) => {
  const raw = Math.max(max, 1) / 3;
  if (isDuration) return DURATION_STEPS.find((step) => step >= raw) || Math.ceil(raw / 36000) * 36000;
  const magnitude = 10 ** Math.floor(Math.log10(raw));
  return [1, 2, 5, 10].map((factor) => factor * magnitude).find((step) => step >= raw);
};

/**
 * The last 12 weeks as columns, Duração or Volume. Bars are buttons: hovering or focusing one shows its value, a
 * tap keeps it (aria-pressed). This week starts selected. On phones only every third date is written under them.
 */
export const WeekChart = ({ logs, isDesktop }) => {
  const format = useProgressFormat();
  const { ui } = format;
  const [metric, setMetric] = useState('duration');
  const [selected, setSelected] = useState(WEEKS - 1);
  const [hovered, setHovered] = useState(null);

  const weeks = useMemo(() => lastWeeks(logs, WEEKS), [logs]);
  const isDuration = metric === 'duration';
  const values = weeks.map((week) => (isDuration ? week.duration : week.volume));
  const step = weekAxisStep(Math.max(...values), isDuration);
  const scaleMax = step * 3;
  const valueText = (value) => (isDuration ? format.trainingTime(value) : `${format.integer(value)} kg`);
  const tickText = (value) => {
    if (!value) return '0';
    if (isDuration) return value < 3600 ? `${Math.round(value / 60)} min` : `${format.number(value / 3600)}h`;
    return value >= 1000 ? ui('thousand', { value: format.number(value / 1000) }) : format.integer(value);
  };
  const heightOf = (value) => (value ? Math.max((value / scaleMax) * 100, 2) : 0);
  const shown = hovered ?? selected;
  const labelEvery = isDesktop ? 1 : 3;

  return (
    <div className="mt-pg-weeks">
      <div className="mt-pg-weeks__head">
        <h3 className="mt-pg-weeks__title">{ui('chartTitle')}</h3>
        <div className="mt-segment mt-pg-metric" role="group" aria-label={ui('chartView')}>
          <button type="button" aria-pressed={isDuration} onClick={() => setMetric('duration')}>
            {ui('duration')}
          </button>
          <button
            type="button"
            aria-pressed={!isDuration}
            onClick={() => {
              if (isDuration) trackEvent('progress_chart_volume', 'Progress - Chart Volume', 'progress', true);
              setMetric('volume');
            }}
          >
            {ui('volumeTab')}
          </button>
        </div>
      </div>
      <div className="mt-pg-wc">
        <ol className="mt-pg-wc__y" aria-hidden="true">
          {[3, 2, 1, 0].map((tick) => (
            <li key={tick} style={{ bottom: `${(tick / 3) * 100}%` }}>
              {tickText(step * tick)}
            </li>
          ))}
        </ol>
        <div className="mt-pg-wc__plot">
          {[1, 2, 3].map((tick) => (
            <i key={tick} className="mt-pg-wc__grid" style={{ bottom: `${(tick / 3) * 100}%` }} aria-hidden="true" />
          ))}
          <ol className="mt-pg-wc__bars">
            {weeks.map((week, index) => (
              <li key={week.start.toISOString()} className={index === WEEKS - 1 ? 'is-current' : undefined}>
                <button
                  type="button"
                  aria-pressed={selected === index}
                  aria-label={ui('chartBar', { week: format.weekLabel(week.start), value: valueText(values[index]) })}
                  onClick={() => setSelected(index)}
                  onPointerEnter={() => setHovered(index)}
                  onPointerLeave={() => setHovered(null)}
                  onFocus={() => setHovered(index)}
                  onBlur={() => setHovered(null)}
                >
                  <span className="mt-pg-wc__bar" style={{ height: `${heightOf(values[index])}%` }} />
                </button>
                {shown === index && (
                  <span className="mt-pg-tip mt-pg-wc__tip" style={{ bottom: `calc(${heightOf(values[index])}% + 8px)` }} aria-hidden="true">
                    {valueText(values[index])}
                  </span>
                )}
              </li>
            ))}
          </ol>
        </div>
        <ol className="mt-pg-wc__x" aria-hidden="true">
          {weeks.map((week, index) => (
            <li key={week.start.toISOString()}>{(WEEKS - 1 - index) % labelEvery === 0 ? format.weekLabel(week.start) : ''}</li>
          ))}
        </ol>
      </div>
    </div>
  );
};

// The plot's width, read before paint and on every resize (charts are drawn in pixels, so text stays its size)
const useWidth = (ref, fallback) => {
  const [width, setWidth] = useState(fallback);
  useLayoutEffect(() => {
    const element = ref.current;
    if (!element) return undefined;
    const measure = () => setWidth(element.clientWidth || fallback);
    measure();
    const observer = typeof ResizeObserver === 'function' ? new ResizeObserver(measure) : null;
    observer?.observe(element);
    return () => observer?.disconnect();
  }, [ref, fallback]);
  return width;
};

const LOAD_STEPS = [1, 2, 2.5, 5, 10];
const PAD = { left: 40, right: 44, top: 14, bottom: 26 };
const MIN_LABEL_GAP = 64;

/**
 * An exercise's best set, session by session: one line over time, records marked, the last value written at its
 * end. Pointer or arrow keys snap a crosshair to the nearest session and say what it was. Month starts (and the
 * first day) are written along the bottom, kept inside the plot so the last never runs off the card.
 */
export const LoadChart = ({ exercise, summary, isDesktop }) => {
  const format = useProgressFormat();
  const { ui } = format;
  const boxRef = useRef(null);
  const tipRef = useRef(null);
  const width = useWidth(boxRef, 320);
  const [active, setActive] = useState(-1);
  const height = isDesktop ? 200 : 170;
  const points = summary.points;

  const chart = useMemo(() => {
    if (!points.length) return null;
    const values = points.map((point) => point.value);
    let low = Math.min(...values);
    let high = Math.max(...values);
    const raw = Math.max((high - low) / 3, 1);
    const magnitude = 10 ** Math.floor(Math.log10(raw));
    const step = LOAD_STEPS.map((factor) => factor * magnitude).find((value) => value >= raw);
    low = Math.max(0, Math.floor(low / step) * step);
    high = Math.max(Math.ceil(high / step) * step, low + step);
    const t0 = points[0].date.getTime();
    const t1 = points[points.length - 1].date.getTime();
    const right = width - PAD.right;
    const x = (date) => (t1 === t0 ? (PAD.left + right) / 2 : PAD.left + ((date.getTime() - t0) / (t1 - t0)) * (right - PAD.left));
    const y = (value) => PAD.top + (1 - (value - low) / (high - low)) * (height - PAD.top - PAD.bottom);
    const ticks = [];
    for (let value = low; value <= high + 1e-9; value += step) ticks.push(value);
    const coords = points.map((point) => [x(point.date), y(point.value)]);
    // The first day and each new month, never closer than a label's width to the one before
    const labels = [];
    const months = new Set();
    points.forEach((point, index) => {
      const key = `${point.date.getFullYear()}-${point.date.getMonth()}`;
      if (months.has(key)) return;
      months.add(key);
      const px = coords[index][0];
      if (labels.length && px - labels[labels.length - 1].x < MIN_LABEL_GAP) return;
      labels.push({ x: px, text: format.shortDate(point.date) });
    });
    const path = coords.map(([px, py], index) => `${index ? 'L' : 'M'}${px.toFixed(1)},${py.toFixed(1)}`).join('');
    const base = height - PAD.bottom;
    const area = `${path}L${coords[coords.length - 1][0].toFixed(1)},${base}L${coords[0][0].toFixed(1)},${base}Z`;
    return { ticks, coords, labels, path, area, y, base };
  }, [points, width, height, format]);

  // The bubble sits over its point, kept inside the plot
  useLayoutEffect(() => {
    const tip = tipRef.current;
    if (!tip || active < 0 || !chart) return;
    const [px, py] = chart.coords[active];
    const tipWidth = tip.offsetWidth;
    tip.style.left = `${Math.min(Math.max(px - tipWidth / 2, 0), width - tipWidth)}px`;
    tip.style.top = `${Math.max(py - 40, -10)}px`;
  }, [active, chart, width]);

  if (!chart) return null;

  const valueText = (value) => (exercise.isLoaded ? `${format.number(value)} kg` : ui('reps', { count: value }));
  const pointText = (point) => [format.shortDate(point.date), format.dose(point.group.best), point.record ? ui('recordShort').toLowerCase() : null].filter(Boolean).join(' · ');
  const nearest = (clientX) => {
    const box = boxRef.current.getBoundingClientRect();
    const px = ((clientX - box.left) / box.width) * width;
    return chart.coords.reduce((best, [cx], index) => (Math.abs(cx - px) < Math.abs(chart.coords[best][0] - px) ? index : best), 0);
  };
  const onKeyDown = (event) => {
    if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') return;
    event.preventDefault();
    const from = active < 0 ? points.length - 1 : active;
    setActive(Math.max(0, Math.min(points.length - 1, from + (event.key === 'ArrowRight' ? 1 : -1))));
  };
  const last = chart.coords[chart.coords.length - 1];
  const first = points[0];
  const end = points[points.length - 1];
  const title = ui(exercise.isLoaded ? 'loadChart' : 'repsChart');

  return (
    <div
      ref={boxRef}
      className="mt-pg-plot"
      tabIndex={0}
      role="group"
      aria-label={ui('chartSummary', {
        title,
        count: points.length,
        from: valueText(first.value),
        fromDate: format.shortDate(first.date),
        to: valueText(end.value),
        toDate: format.shortDate(end.date),
      })}
      onPointerMove={(event) => setActive(nearest(event.clientX))}
      onPointerLeave={() => setActive(-1)}
      onFocus={() => setActive(points.length - 1)}
      onBlur={() => setActive(-1)}
      onKeyDown={onKeyDown}
    >
      <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} aria-hidden="true">
        {chart.ticks.map((tick) => (
          <g key={tick}>
            <line className="mt-pg-plot__grid" x1={PAD.left} x2={width} y1={chart.y(tick)} y2={chart.y(tick)} />
            <text x={PAD.left - 8} y={chart.y(tick) + 4} textAnchor="end">
              {format.number(tick)}
            </text>
          </g>
        ))}
        <line className="mt-pg-plot__base" x1={PAD.left} x2={width} y1={chart.base} y2={chart.base} />
        {chart.labels.map((label, index) => {
          const isFirst = index === 0 && label.x < PAD.left + 24;
          const isLast = label.x > width - PAD.right - 24;
          return (
            <text key={label.text} x={isLast ? Math.min(label.x + 6, width) : label.x} y={chart.base + 19} textAnchor={isFirst ? 'start' : isLast ? 'end' : 'middle'}>
              {label.text}
            </text>
          );
        })}
        <path className="mt-pg-plot__area" d={chart.area} />
        <path className="mt-pg-plot__line" d={chart.path} />
        {active >= 0 && <line className="mt-pg-plot__cross" x1={chart.coords[active][0]} x2={chart.coords[active][0]} y1={PAD.top} y2={chart.base} />}
        {points.map((point, index) => (
          <circle
            key={`${point.log.id}-${index}`}
            className={point.record ? 'mt-pg-plot__dot mt-pg-plot__dot--record' : 'mt-pg-plot__dot'}
            cx={chart.coords[index][0]}
            cy={chart.coords[index][1]}
            r={index === active ? 6 : point.record ? 5 : 4}
          />
        ))}
        <text className="mt-pg-plot__end" x={last[0] + 9} y={last[1] + 4}>
          {exercise.isLoaded ? format.number(end.value) : end.value}
        </text>
      </svg>
      {active >= 0 && (
        <span ref={tipRef} className="mt-pg-tip mt-pg-plot__tip" aria-hidden="true">
          {pointText(points[active])}
        </span>
      )}
      <span className="mt-sr-only" aria-live="polite">
        {active >= 0 ? pointText(points[active]) : ''}
      </span>
    </div>
  );
};

// A 64 × 24 line of the best set per session, its last point marked
export const Sparkline = ({ values }) => {
  const W = 64;
  const H = 24;
  if (!values.length) return null;
  const low = Math.min(...values);
  const high = Math.max(...values);
  const x = (index) => (values.length === 1 ? W : (index / (values.length - 1)) * W);
  const y = (value) => (high === low ? H / 2 : H - 2 - ((value - low) / (high - low)) * (H - 4));
  const line = values.map((value, index) => `${x(index).toFixed(1)},${y(value).toFixed(1)}`).join(' ');
  return (
    <svg className="mt-pg-spark" width={W} height={H} viewBox={`0 0 ${W} ${H}`} aria-hidden="true">
      <polyline points={line} />
      <circle cx={x(values.length - 1)} cy={y(values[values.length - 1])} r="3.5" />
    </svg>
  );
};
